/**
 * Allègement d'un enregistrement avant son envoi.
 *
 * Le dépôt se fait depuis Conakry, souvent en partage de connexion. Un atelier
 * filmé au téléphone pèse plusieurs centaines de mégaoctets dont la quasi
 * totalité est de l'image, que la transcription n'utilise pas. On ne garde que
 * le son, ramené à une voix mono en seize kilohertz : c'est la définition sur
 * laquelle travaillent les moteurs de transcription, en envoyer davantage ne
 * les aide pas.
 *
 * L'allègement est facultatif par construction. Si le navigateur ne sait pas
 * décoder le fichier, si l'enregistrement est trop long pour être décodé sans
 * risque, ou si le résultat ne serait pas plus léger que l'original, on renvoie
 * le fichier tel quel : mieux vaut un envoi lent qu'un dépôt impossible.
 */

/** Définition retenue : celle sur laquelle travaillent les moteurs. */
const FREQUENCE = 16000;

/**
 * Au-delà, le décodage tiendrait plusieurs centaines de mégaoctets en mémoire
 * et ferait tomber l'onglet sur une machine modeste. Le serveur sait de toute
 * façon découper les enregistrements longs.
 */
const DUREE_MAX = 90 * 60;

/** En deçà de ce gain, l'attente du recodage ne vaut pas les octets économisés. */
const GAIN_MINIMUM = 0.8;

/**
 * Durée d'un fichier, lue dans ses métadonnées.
 *
 * Se lit sans télécharger ni décoder le contenu, ce qui permet de décider s'il
 * vaut la peine d'aller plus loin.
 */
export function dureeDe(fichier: File): Promise<number> {
  return new Promise((resoudre) => {
    const url = URL.createObjectURL(fichier);
    const element = document.createElement('audio');
    element.preload = 'metadata';

    const terminer = (duree: number) => {
      URL.revokeObjectURL(url);
      resoudre(duree);
    };

    element.onloadedmetadata = () => terminer(
      Number.isFinite(element.duration) ? element.duration : 0,
    );
    element.onerror = () => terminer(0);
    element.src = url;
  });
}

/** Mélange les canaux en une seule voix : une réunion n'a pas de stéréo utile. */
function enMono(mesures: AudioBuffer): Float32Array {
  const longueur = mesures.length;
  if (mesures.numberOfChannels === 1) return mesures.getChannelData(0);

  const canaux = Array.from(
    { length: mesures.numberOfChannels },
    (_, i) => mesures.getChannelData(i),
  );
  const mono = new Float32Array(longueur);
  for (let i = 0; i < longueur; i += 1) {
    let somme = 0;
    for (let c = 0; c < canaux.length; c += 1) somme += canaux[c][i];
    mono[i] = somme / canaux.length;
  }
  return mono;
}

/** Emballe les mesures en WAV seize bits, le format que tout moteur accepte. */
function enWav(mono: Float32Array, frequence: number): Blob {
  const octets = new ArrayBuffer(44 + mono.length * 2);
  const vue = new DataView(octets);

  const texte = (position: number, valeur: string) => {
    for (let i = 0; i < valeur.length; i += 1) vue.setUint8(position + i, valeur.charCodeAt(i));
  };

  texte(0, 'RIFF');
  vue.setUint32(4, 36 + mono.length * 2, true);
  texte(8, 'WAVE');
  texte(12, 'fmt ');
  vue.setUint32(16, 16, true);          // taille du bloc de format
  vue.setUint16(20, 1, true);           // PCM non compressé
  vue.setUint16(22, 1, true);           // un seul canal
  vue.setUint32(24, frequence, true);
  vue.setUint32(28, frequence * 2, true); // octets par seconde
  vue.setUint16(32, 2, true);           // octets par échantillon
  vue.setUint16(34, 16, true);          // bits par échantillon
  texte(36, 'data');
  vue.setUint32(40, mono.length * 2, true);

  for (let i = 0; i < mono.length; i += 1) {
    const borne = Math.max(-1, Math.min(1, mono[i]));
    vue.setInt16(44 + i * 2, borne < 0 ? borne * 0x8000 : borne * 0x7fff, true);
  }
  return new Blob([octets], { type: 'audio/wav' });
}

export interface Allegement {
  fichier: File | Blob;
  /** Vrai si le fichier a été recodé ; faux s'il part tel quel. */
  allege: boolean;
  /** Taille de départ, pour dire à l'utilisateur ce qui a été économisé. */
  octetsOrigine: number;
  octetsEnvoyes: number;
}

/**
 * Rend la version la plus légère de l'enregistrement qu'on sache produire.
 *
 * Ne lève jamais : un allègement impossible n'est pas une erreur, c'est un
 * envoi plus long.
 */
export async function alleger(fichier: File): Promise<Allegement> {
  const telQuel: Allegement = {
    fichier,
    allege: false,
    octetsOrigine: fichier.size,
    octetsEnvoyes: fichier.size,
  };

  const duree = await dureeDe(fichier);
  if (!duree || duree > DUREE_MAX) return telQuel;

  // Taille du WAV qu'on produirait : seize bits, mono, seize kilohertz.
  const attendu = 44 + Math.ceil(duree * FREQUENCE) * 2;
  if (attendu > fichier.size * GAIN_MINIMUM) return telQuel;

  try {
    const Contexte = window.AudioContext || (window as any).webkitAudioContext;
    if (!Contexte) return telQuel;

    // Le contexte est ouvert à seize kilohertz : le décodage rééchantillonne
    // au passage, sans nous faire tenir la version pleine définition.
    const contexte = new Contexte({ sampleRate: FREQUENCE });
    try {
      const mesures = await contexte.decodeAudioData(await fichier.arrayBuffer());
      const wav = enWav(enMono(mesures), mesures.sampleRate);
      if (wav.size >= fichier.size) return telQuel;

      const nom = fichier.name.replace(/\.[^.]+$/, '') || 'reunion';
      return {
        fichier: new File([wav], `${nom}.wav`, { type: 'audio/wav' }),
        allege: true,
        octetsOrigine: fichier.size,
        octetsEnvoyes: wav.size,
      };
    } finally {
      contexte.close();
    }
  } catch {
    // Format que le navigateur ne sait pas décoder : le serveur s'en chargera.
    return telQuel;
  }
}

/** Taille lisible, pour annoncer ce qui part sur le réseau. */
export function enOctetsLisibles(octets: number): string {
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} ko`;
  return `${(octets / (1024 * 1024)).toFixed(octets < 10 * 1024 * 1024 ? 1 : 0)} Mo`;
}
